/**
 * Endpoint path builders for the Prism transaction lifecycle, providing raw string segments for both GET (query) and POST/PUT/DELETE (mutation) API calls.
 *
 * Properties fall into four sections by HTTP method (GET, POST, PUT, DELETE). Some return plain strings without the ENV prefix; those that need a parameter accept a function argument to append an ID.
 */
export const prismTransactionEndpoints = {
  /* GET */

  getUserAppAccess: 'GetUserAppAccess',
  getUserRoles: 'GetUserRoles',
  getAppUserPreferences: 'user/preferences',
  getPropertyByPropertyId: (propertyId: number) => `GetPropertyByPropertyId/${propertyId}`,
  getPropertiesByTransactionId: (transactionId: number) => `GetPropertiesByTransactionId/${transactionId}`,
  getPrismTransactions: 'GetPrismTransactions',
  getTransactionsReportNotesBulk: 'GetTransactionsReportNotesBulk',
  getTransactionTasks: (transactionId: number) => `GetTransactionTasks/${transactionId}`,
  /**
   * PRM-555 — every open task assigned to the calling user, across all
   * transactions they can see. Scoped server-side from the bearer token.
   */
  getMyTasks: 'GetMyTasks',
  getPrismTransactionsByInvestmentPhase: 'GetPrismTransactionsByInvestmentPhase',
  /** PRM-520 — denormalized Transaction Table read projection (`GetTransactionTable` function, route `TransactionTable`). */
  getTransactionTable: 'TransactionTable',
  getPrismTransactionById: (transactionId: number) => `GetPrismTransactionById/${transactionId}`,
  createTransactionFolders: (transactionId: number, division: string, title: string) =>
    `CreateTransactionFolders/${transactionId}/${encodeURIComponent(division)}/${encodeURIComponent(title)}`,
  getPropertyPricesByPropertyId: `GetPropertyPricesByPropertyId`,
  getTransactionPricesByTransactionIdAndInvestmentPhaseId:
    'GetTransactionPricesByTransactionIdAndInvestmentPhaseId',
  getTransactionPriceTypes: 'GetTransactionPriceTypes',
  getInvestmentPhases: 'GetInvestmentPhases',
  getChecklistMilestones: 'GetChecklistMilestones',
  getTaskDefinitions: 'GetTaskDefinitions',
  getPhaseMilestoneSyncStatus: (transactionId: number) =>
    `GetPhaseMilestoneSyncStatus/${transactionId}`,
  getCountries: 'GetCountries',
  getStateProvinces: 'GetStateProvinces',
  getStateProvincesByCountryId: (countryId: number) => `GetStateProvincesByCountryId/${countryId}`,
  getCountiesByStateProvinceId: (stateProvinceId: number) =>
    `GetCountiesByStateProvinceId/${stateProvinceId}`,
  getAssetTypes: 'GetAssetTypes',
  getCurrencies: 'GetCurrencies',
  getFxRateByCurrency: (currency: string) => `GetFxRateByCurrency/${currency}`,
  getManagementTypes: 'GetManagementTypes',
  getTransactionTypes: 'GetTransactionTypes',
  getSaleTypes: 'GetSaleTypes',
  getTransactionHealths: 'GetTransactionHealths',
  getReasonCodes: 'GetReasonCodes',
  getTransactionPhaseNotesByTransactionId: 'GetTransactionPhaseNotesByTransactionId',
  getTransactionPhaseNotesByTransactionIdAndInvestmentPhaseId:
    'GetTransactionPhaseNotesByTransactionIdAndInvestmentPhaseId',
  getPrismLeads: 'GetPrismLeads',
  getChangeLogByTransactionId: (transactionId: number) => `GetChangeLogByTransactionId/${transactionId}`,
  getPrismTransactionPhaseSnapshots: (transactionId: number) =>
    `PrismTransactionPhaseSnapshots/${transactionId}`,
  getTransactionExportHistory: (transactionId: number) =>
    `TransactionExports/${transactionId}`,
  getReports: 'Reports',
  getReportById: (reportInstanceId: number) => `Reports/${reportInstanceId}`,
  exportReport: (reportInstanceId: number) => `Reports/${reportInstanceId}/export`,
  getVersion: 'release',

  /* POST */

  recordTransactionExport: 'TransactionExports',
  triggerReportGeneration: 'Dev/Reports/Trigger',
  approveFinanceLead: (transactionId: number) => `ApproveFinanceLead/${transactionId}`,
  approveInvestmentAnalyst: (transactionId: number) => `ApproveInvestmentAnalyst/${transactionId}`,
  approveTransactionLead: (transactionId: number) => `ApproveTransactionLead/${transactionId}`,
  cancelPrismTransaction: (transactionId: number) => `CancelPrismTransaction/${transactionId}`,  cancelProperty: (propertyId: number) => `CancelProperty/${propertyId}`,
  clonePrismTransaction: (transactionId: number) => `ClonePrismTransaction/${transactionId}`,
  completeAnalysis: (transactionId: number) => `CompleteAnalysis/${transactionId}`,
  completeDueDiligence: (transactionId: number) => `CompleteDueDiligence/${transactionId}`,
  completeInternalApprovals: (transactionId: number) => `CompleteInternalApprovals/${transactionId}`,
  completeNegotiation: (transactionId: number) => `CompleteNegotiation/${transactionId}`,
  completeTransactionClosing: (transactionId: number) => `CompleteTransactionClosing/${transactionId}`,
  completeTransactionPostClose: (transactionId: number) => `CompleteTransactionPostClose/${transactionId}`,
  completeTransactionPhaseTask: (phaseTaskId: number) => `CompleteTransactionPhaseTask/${phaseTaskId}`,
  editTransactionPhaseTaskData: (phaseTaskId: number) => `EditTransactionPhaseTaskData/${phaseTaskId}`,
  addTransactionPhaseTask: (transactionId: number, taskDefinitionId: number) =>
    `AddTransactionPhaseTask/${transactionId}/${taskDefinitionId}`,
  removeTransactionPhaseTask: (transactionId: number, transactionPhaseTaskId: number) =>
    `RemoveTransactionPhaseTask/${transactionId}/${transactionPhaseTaskId}`,
  declineCeo: (transactionId: number) => `DeclineCeo/${transactionId}`,
  declineOperations: (transactionId: number) => `DeclineOperations/${transactionId}`,
  failPrismTransaction: (transactionId: number) => `FailPrismTransaction/${transactionId}`,
  lowerPrismTransactionHealth: (transactionId: number) => `LowerPrismTransactionHealth/${transactionId}`,
  mapProperty: (propertyId: number) => `MapProperty/${propertyId}`,
  mapTransaction: (transactionId: number) => `MapTransaction/${transactionId}`,
  markInvestmentPhaseComplete: (transactionId: number) => `MarkInvestmentPhaseComplete/${transactionId}`,
  postPrismTransaction: 'PostPrismTransaction',
  postProperty: (transactionId: number) => `PostProperty/${transactionId}`,
  postPrismTransactionJarvisData: (transactionId: number) =>
    `PostPrismTransactionJarvisData/${transactionId}`,
  postPropertyPrices: 'PostPropertyPrices',
  postTransactionPriceType: 'PostTransactionPriceType',
  postTransactionPhaseNote: 'PostTransactionPhaseNote',
  upsertAppUserPreferences: 'user/preferences',
  patchAppUserPreferences: 'user/preferences',
  qualifyCeo: (transactionId: number) => `QualifyCeo/${transactionId}`,
  qualifyOperations: (transactionId: number) => `QualifyOperations/${transactionId}`,
  raisePrismTransactionHealth: (transactionId: number) => `RaisePrismTransactionHealth/${transactionId}`,
  reactivateProperty: (propertyId: number) => `ReactivateProperty/${propertyId}`,
  rejectPrismTransaction: (transactionId: number) => `RejectPrismTransaction/${transactionId}`,
  removePrismTransactionJarvisData: (transactionId: number) =>
    `RemovePrismTransactionJarvisData/${transactionId}`,
  revertInvestmentPhase: (transactionId: number) => `RevertInvestmentPhase/${transactionId}`,
  repairPhaseMilestoneSync: (transactionId: number) =>
    `RepairPhaseMilestoneSync/${transactionId}`,
  submitPrismTransaction: (transactionId: number) => `SubmitPrismTransaction/${transactionId}`,
  updateTransactionTeamLead: (transactionId: number) => `UpdateTransactionTeamLead/${transactionId}`,
  lockPrismTransaction: (transactionId: number) => `LockPrismTransaction/${transactionId}`,
  unlockPrismTransaction: (transactionId: number) => `UnlockPrismTransaction/${transactionId}`,

  /* PUT */

  putPrismTransaction: `PutPrismTransaction`,
  putPrismTransactionPhaseSnapshot: `PutPrismTransactionPhaseSnapshot`,
  putProperty: `PutProperty`,
  putPropertyPrice: 'PutPropertyPrice',
  updatePropertyIdentifier: 'UpdatePropertyIdentifier',
  putTransactionPriceType: 'PutTransactionPriceType',
  putTransactionPhaseNote: 'PutTransactionPhaseNote',
  updateTaskDefinition: (taskDefinitionId: number) => `UpdateTaskDefinitions/${taskDefinitionId}`,
  /** PRM-555 — set/clear a single task's assignee (PrismCore AppUser.Id). */
  updateTransactionPhaseTaskAssignee: (phaseTaskId: number) =>
    `UpdateTransactionPhaseTaskAssignee/${phaseTaskId}`,
  /** PRM-555 — set/clear a single task's due date. */
  updateTransactionPhaseTaskDueDate: (phaseTaskId: number) =>
    `UpdateTransactionPhaseTaskDueDate/${phaseTaskId}`,
  /** PRM-555 — bypass a single task's prerequisite dependency. */
  setTransactionPhaseTaskPrerequisiteOverride: (phaseTaskId: number) =>
    `SetTransactionPhaseTaskPrerequisiteOverride/${phaseTaskId}`,
  reorderTaskDefinitions: 'ReorderTaskDefinitions',
  createTaskDefinition: 'CreateTaskDefinition',

  /* DELETE */

  deleteProperty: 'DeleteProperty',
  deletePropertyPrice: (id: number) => `DeletePropertyPrice/${id}`,
  deleteTransactionPhaseNote: (noteId: number) => `DeleteTransactionPhaseNote/${noteId}`,

  /* DEV */

  refreshInvestmentData: 'RefreshInvestmentData',
};
